import { type TypedDocumentNode, gql } from '@apollo/client';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
} from 'date-fns';
import {
  FormattedMessage,
  FormattedNumber,
  IntlFormatters,
  useIntl,
} from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Clock } from '@sorare/core/src/atoms/icons/Clock';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import { isType } from '@sorare/core/src/gql';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { zeroMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { glossary } from '@sorare/core/src/lib/glossary';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import type { OnboardingPromoRecapDialog_so5OnboardingPromotionalEvent } from './__generated__/index.graphql';
import banner from './assets/banner.png';
import marketCredit from './assets/marketCredit.png';

const now = new Date();
const formatTimeLeft = (
  endDate: Date,
  formatMessage: IntlFormatters['formatMessage']
) => {
  const daysLeft = differenceInDays(endDate, now);
  if (daysLeft >= 1)
    return formatMessage(
      {
        id: 'earlyAccess.formatTimeLeft.1dayOrMore',
        defaultMessage:
          '{daysLeft} {daysLeft, plural, one {day left} other {days left}}',
      },
      {
        daysLeft,
      }
    );

  const hoursLeft = differenceInHours(endDate, now);
  if (hoursLeft >= 1)
    return formatMessage(
      {
        id: 'earlyAccess.formatTimeLeft.1hourOrMore',
        defaultMessage:
          '{hoursLeft} {hoursLeft, plural, one {hour left} other {hours left}}',
      },
      {
        hoursLeft,
      }
    );
  const minutesLeft = differenceInMinutes(endDate, now);
  return formatMessage(
    {
      id: 'earlyAccess.formatTimeLeft.1minuteOrMore',
      defaultMessage:
        '{minutesLeft} {minutesLeft, plural, one {minute left} other {minutes left}}',
    },
    {
      minutesLeft,
    }
  );
};

const Root = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  isolation: isolate;
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  top: var(--unit);
  right: var(--unit);
  z-index: 1;
`;
const TimeLeftBanner = styled(Horizontal).attrs({ center: true, gap: 0.5 })`
  background: linear-gradient(
    101deg,
    #000040 -0.61%,
    #0047ff 45.52%,
    #fc00fc 96.07%
  );
  padding: var(--unit);
`;
const UppercaseLabelS = styled(LabelS)`
  text-transform: uppercase;
`;
const MarketCreditImg = styled.img`
  width: calc(5 * var(--unit));
  height: calc(5 * var(--unit));
  object-fit: contain;
`;
const TimelineWrapper = styled(Vertical).attrs({ gap: 0 })`
  --dot-width: 40px;
  padding: var(--triple-unit) var(--double-and-a-half-unit) var(--triple-unit)
    var(--double-unit);
`;
const TimelineRowWrapper = styled(Horizontal)`
  align-items: flex-start;
  position: relative;
  min-height: 64px;
`;
const VerticalLine = styled.div`
  --line-width: 10px;
  position: absolute;
  width: var(--line-width);
  top: calc(var(--dot-width) - 1px);
  left: calc(var(--dot-width) / 2 - var(--line-width) / 2);
  bottom: -1px;
  background: var(--c-white);
`;
const RowContent = styled.div`
  padding-top: var(--unit);
`;
const Dot = styled(Horizontal).attrs({ gap: 0, center: true })`
  min-width: var(--dot-width);
  min-height: var(--dot-width);
  border-radius: 50%;
  box-sizing: border-box;
  z-index: 1;
  background-color: var(--c-white);
  color: var(--c-brand-600);
`;

type Props = {
  open: boolean;
  onClose: () => void;
  onboardingPromotionalEvent: OnboardingPromoRecapDialog_so5OnboardingPromotionalEvent;
};
export const OnboardingPromoRecapDialog = ({
  open,
  onClose,
  onboardingPromotionalEvent,
}: Props) => {
  const { endDate, myTask } = onboardingPromotionalEvent;
  const { formatMessage } = useIntl();

  const configReward = myTask?.configRewards.at(0);

  const discount =
    configReward &&
    isType(configReward, 'ProbabilisticBundleConfigReward') &&
    configReward.probabilisticBundleConfig.slots.at(0)?.probableConfigs.at(0)
      ?.conversionCredit?.maxDiscount;

  const { main: discountAmount } = useAmountWithConversion({
    monetaryAmount: discount || zeroMonetaryAmount,
  });

  return (
    <Dialog open={open} fullWidth maxWidth="xs">
      {({ CloseButton }) => (
        <Root>
          <CloseButtonWrapper>
            <CloseButton onClose={onClose} />
          </CloseButtonWrapper>
          <img src={banner} alt="" />
          <TimeLeftBanner>
            <Clock width={12} height={12} />
            <LabelS bold>{formatTimeLeft(endDate, formatMessage)}</LabelS>
          </TimeLeftBanner>
          <TimelineWrapper>
            <TimelineRowWrapper>
              <Dot>
                <FontAwesomeIcon icon={faCheck} />
              </Dot>
              <VerticalLine />
              <RowContent>
                <LabelL bold>
                  <FormattedMessage {...glossary.signup} />
                </LabelL>
              </RowContent>
            </TimelineRowWrapper>
            <TimelineRowWrapper>
              <Dot>
                <LabelL bold>
                  <FormattedNumber value={2} />
                </LabelL>
              </Dot>
              <RowContent>
                <Vertical gap={2}>
                  <LabelL bold>{myTask?.description}</LabelL>
                  <Vertical>
                    <UppercaseLabelS bold color="var(--c-rivals-win)">
                      <FormattedMessage
                        id="OnboardingPromoRecapDialog.yourGiftIsWaiting"
                        defaultMessage="Your Welcome Gift is waiting"
                      />
                    </UppercaseLabelS>
                    {discountAmount ? (
                      <Horizontal>
                        <MarketCreditImg src={marketCredit} alt="" />
                        <LabelL bold>
                          <FormattedMessage
                            id="OnboardingPromoRecapDialog.promo"
                            defaultMessage="{discountAmount} off your first Limited Card"
                            values={{
                              discountAmount,
                            }}
                          />
                        </LabelL>
                      </Horizontal>
                    ) : null}
                  </Vertical>
                  <Button color="primary" size="medium" onClick={onClose}>
                    <FormattedMessage
                      id="OnboardingPromoRecapDialog.selectPlayers"
                      defaultMessage="Select Players"
                    />
                  </Button>
                </Vertical>
              </RowContent>
            </TimelineRowWrapper>
          </TimelineWrapper>
        </Root>
      )}
    </Dialog>
  );
};

OnboardingPromoRecapDialog.fragments = {
  so5OnboardingPromotionalEvent: gql`
    fragment OnboardingPromoRecapDialog_so5OnboardingPromotionalEvent on So5OnboardingPromotionalEvent {
      id
      endDate
      myTask {
        id
        description
        configRewards {
          ... on ProbabilisticBundleConfigReward {
            probabilisticBundleConfig {
              id
              slots {
                probableConfigs {
                  conversionCredit {
                    id
                    maxDiscount {
                      ...MonetaryAmountFragment_monetaryAmount
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<OnboardingPromoRecapDialog_so5OnboardingPromotionalEvent>,
};
